(() => {
  // ── per-slide media helpers ──────────────────────────────
  const playSlideVideo = (slide) => {
    const v = slide?.querySelector('.slideshow__video');
    if (v) v.play().catch(() => {});
  };

  const stopSlideVideo = (slide) => {
    const v = slide?.querySelector('.slideshow__video');
    if (v) v.pause();
  };

  // ── DOM helpers ──────────────────────────────────────────
  const getMedia = (slide) =>
    slide.querySelector('.slideshow__image, .slideshow__video');

  // ── animation constants ──────────────────────────────────
  const T = 1.3;            // clip-path + media transition (s)
  const E = 'power3.inOut'; // cinematic ease

  // ── luxury GSAP slider ───────────────────────────────────
  const initSlider = (box) => {
    if (typeof gsap === 'undefined') return;

    const swiperEl = box.querySelector('.slideshow__swiper--overlay');
    const wrapper  = swiperEl?.querySelector('.swiper-wrapper');
    if (!wrapper) return;

    const slides = Array.from(wrapper.querySelectorAll(':scope > .swiper-slide'));
    if (slides.length < 2) return;

    const hasAutoplay   = box.getAttribute('data-autoplay') === 'true';
    const isLoop        = hasAutoplay;
    const hasPagination = box.getAttribute('data-pagination') === 'true';
    const paginationEl  = hasPagination ? box.querySelector('.swiper-pagination') : null;
    const btnNext       = box.querySelector('.swiper-button-next');
    const btnPrev       = box.querySelector('.swiper-button-prev');

    let current   = 0;
    let busy      = false;
    let autoTimer = null;

    // ── create progressbar fill element (Swiper used to do this) ─
    if (paginationEl) {
      paginationEl.classList.add('swiper-pagination-progressbar');
      if (!paginationEl.querySelector('.swiper-pagination-progressbar-fill')) {
        const fill = document.createElement('span');
        fill.className = 'swiper-pagination-progressbar-fill';
        paginationEl.appendChild(fill);
      }
    }

    // ── stack slides with absolute positioning ───────────
    gsap.set(wrapper, { position: 'relative', overflow: 'hidden', clearProps: 'transform' });

    slides.forEach((slide) => {
      const media = getMedia(slide);
      gsap.set(slide, { position: 'absolute', inset: 0, zIndex: 1, clipPath: 'inset(100% 0 0 0)' });
      if (media) gsap.set(media, { scale: 1.2, y: -40, transformOrigin: 'center center' });
    });

    // first slide — reveal immediately
    gsap.set(slides[0], { zIndex: 2, clipPath: 'inset(0% 0 0 0)' });
    const firstMedia = getMedia(slides[0]);
    if (firstMedia) gsap.set(firstMedia, { scale: 1, y: 0 });
    playSlideVideo(slides[0]);

    // ── core transition ──────────────────────────────────
    const go = (idx) => {
      if (busy || idx === current) return;
      busy = true;
      if (hasAutoplay) _killProgress();

      const from      = slides[current];
      const to        = slides[idx];
      const fromMedia = getMedia(from);
      const toMedia   = getMedia(to);

      // layer: outgoing at z=2, incoming at z=3
      gsap.set(from, { zIndex: 2 });
      gsap.set(to, { zIndex: 3, clipPath: 'inset(100% 0 0 0)' });
      if (toMedia) gsap.set(toMedia, { scale: 1.2, y: -40 });

      const tl = gsap.timeline({
        onComplete() {
          current = idx;
          busy    = false;
          // reset outgoing to standby state
          gsap.set(from, { zIndex: 1, clipPath: 'inset(100% 0 0 0)' });
          if (fromMedia) gsap.set(fromMedia, { scale: 1.2, y: -40 });
          stopSlideVideo(from);
          playSlideVideo(to);
          if (!isLoop) {
            box.classList.toggle('slideshow--is-beggining', idx === 0);
            box.classList.toggle('slideshow--is-end', idx === slides.length - 1);
          }
          if (hasAutoplay) { _startProgress(); _scheduleNext(); }
          else _updateProgress(idx);
        },
      });

      // outgoing: image breathes out with subtle Ken Burns
      if (fromMedia) {
        tl.to(fromMedia, { scale: 1.08, duration: T, ease: 'power1.inOut' }, 0);
      }

      // incoming: clip-path curtain rises bottom → top
      tl.to(to, { clipPath: 'inset(0% 0 0 0)', duration: T, ease: E }, 0.04);

      // incoming: image settles into place (scale + translateY)
      if (toMedia) {
        tl.to(toMedia, { scale: 1, y: 0, duration: T, ease: E }, 0.04);
      }
    };

    const next = () => go((current + 1) % slides.length);
    const prev = () => go((current - 1 + slides.length) % slides.length);

    // ── progress bar ─────────────────────────────────────
    const _getDelay = () =>
      parseInt(slides[current]?.dataset.swiperAutoplay || '0', 10) || 6000;

    const _startProgress = () => {
      if (!paginationEl) return;
      const fill = paginationEl.querySelector('.swiper-pagination-progressbar-fill');
      if (!fill) return;
      const delay = _getDelay();
      fill.style.transition = 'none';
      fill.style.transformOrigin = 'top';
      fill.style.transform = 'scaleY(0)';
      fill.getBoundingClientRect();
      fill.style.transition = `transform ${delay}ms linear`;
      fill.style.transform = 'scaleY(1)';
    };

    const _updateProgress = (idx) => {
      if (!paginationEl) return;
      const fill = paginationEl.querySelector('.swiper-pagination-progressbar-fill');
      if (!fill) return;
      const ratio = (idx + 1) / slides.length;
      fill.style.transition = 'transform 0.4s ease';
      fill.style.transformOrigin = 'top';
      fill.style.transform = `scaleY(${ratio})`;
    };

    const _killProgress = () => {
      if (!paginationEl) return;
      const fill = paginationEl.querySelector('.swiper-pagination-progressbar-fill');
      if (!fill) return;
      fill.style.transition = 'none';
      fill.style.transformOrigin = 'top';
      fill.style.transform = 'scaleY(0)';
    };

    // ── autoplay ─────────────────────────────────────────
    const _scheduleNext  = () => {
      if (!hasAutoplay) return;
      clearTimeout(autoTimer);
      autoTimer = setTimeout(next, _getDelay());
    };
    const _pauseAutoplay  = () => clearTimeout(autoTimer);
    const _resumeAutoplay = () => {
      if (hasAutoplay && !busy) { _startProgress(); _scheduleNext(); }
    };

    // ── navigation events ────────────────────────────────
    btnNext?.addEventListener('click', next);
    btnPrev?.addEventListener('click', prev);

    // ── init state ───────────────────────────────────────
    box.classList.add('swiper-initialized');
    if (!isLoop) box.classList.add('slideshow--is-beggining');
    if (hasAutoplay) { requestAnimationFrame(_startProgress); _scheduleNext(); }
    else requestAnimationFrame(() => _updateProgress(0));

    // ── pause/resume on visibility ───────────────────────
    new IntersectionObserver((entries) => {
      if (entries[0].isIntersecting) _resumeAutoplay();
      else { _pauseAutoplay(); _killProgress(); }
    }).observe(box);
  };

  // ── height: 100svh − header − announcement if not overlay ─
  const adjustSlideshowHeight = (box) => {
    const main = document.getElementById('MainContent');
    if (!main) return;
    if (!main.querySelector('.shopify-section')?.contains(box)) return;
    if (document.querySelector('.header--overlay-mode')) return;
    box.style.setProperty(
      '--slideshow-height',
      'calc(100svh - var(--header-height, 0px) - var(--announcement-height, 0px))'
    );
  };

  // ── section lifecycle ────────────────────────────────────
  const initSection = (section) => {
    if (!section?.classList.contains('slideshow-section')) return;
    const box = section.querySelector('.slideshow');
    if (!box) return;
    initSlider(box);
    adjustSlideshowHeight(box);
  };

  initSection(document.currentScript.parentElement);
  document.addEventListener('shopify:section:load', (e) => initSection(e.target));
})();
